import { useContext, useState } from 'react'
import { Users as UsersIcon, ChevronRight } from 'lucide-react'
import { api, ApiError } from '../lib/api'
import { useResource } from '@retink/web-ui'
import { statusTone, type Tone } from '@retink/web-ui'
import { stopRowActivate } from '@retink/web-ui'
import { AuthContext } from '@retink/web-auth'
import { Badge } from '@retink/web-ui'
import { Card, Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@retink/web-ui'
import { ConfirmDestructive } from '@retink/web-ui'
import { RowActions, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from '@retink/web-ui'
import { PageHeader } from '@retink/web-ui'
import { EmptyState, TableEmpty } from '@retink/web-ui'
import { LoadingState, ErrorState } from '@retink/web-ui'
import { toast } from '@retink/web-ui'

type PlatformRole = 'admin' | 'viewer' | null

interface Identity {
  id: string
  email: string
  display_name: string | null
  platform_role: PlatformRole
  status: string
  last_login_at: string | null
  created_at: string
}

const roleLabel = (r: PlatformRole) => (r === 'admin' ? 'Admin' : r === 'viewer' ? 'Viewer' : 'Customer user')
const roleTone = (r: PlatformRole): Tone => (r === 'admin' ? 'primary' : r === 'viewer' ? 'outline' : 'neutral')

// Copy + styling for the confirm step, keyed on the target role.
function describe(email: string, next: PlatformRole) {
  if (next === 'admin') {
    return {
      title: `Make ${email} a platform admin?`,
      body: 'Grants full Retink god-mode across every org and tenant, including write access. Only give this to Retink staff.',
      label: 'Grant admin',
      variant: 'default' as const,
    }
  }
  if (next === 'viewer') {
    return {
      title: `Make ${email} a platform viewer?`,
      body: 'Grants read-only Retink staff access across every org and tenant. They can see everything but change nothing.',
      label: 'Grant viewer',
      variant: 'default' as const,
    }
  }
  return {
    title: `Remove platform access for ${email}?`,
    body: 'Steps them down to a regular customer user. Their current sessions are revoked immediately, so staff access stops at once.',
    label: 'Remove access',
    variant: 'destructive' as const,
  }
}

// Every identity on the platform (Retink staff + all customer users). Platform
// admins promote/step-down staff here by setting identity.platform_role; viewers
// see the list read-only. The server enforces both the write-gate and the
// last-admin / no-self-change guards.
export function Users({ staffOnly = false }: { staffOnly?: boolean }) {
  const { user } = useContext(AuthContext)
  const isAdmin = user?.platformRole === 'admin'
  const { data: people = [], loading, error, reload } = useResource(() => api.get<Identity[]>('/api/admin/identities'), [])
  const [pending, setPending] = useState<{ person: Identity; next: PlatformRole } | null>(null)
  const visiblePeople = staffOnly ? people.filter((person) => person.platform_role !== null) : people

  async function applyRole() {
    if (!pending) return
    try {
      await api.patch(`/api/admin/identities/${pending.person.id}`, { platformRole: pending.next })
      toast.success(
        pending.next === null
          ? `Removed platform access for ${pending.person.email}`
          : `${pending.person.email} is now a platform ${pending.next}`,
      )
      reload()
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Failed to update role')
      throw err // keep the confirm dialog open on failure
    }
  }

  const copy = pending ? describe(pending.person.email, pending.next) : null

  return (
    <div className="p-8">
      <PageHeader
        title={staffOnly ? 'Staff' : 'Users'}
        description={staffOnly
          ? 'Retink platform administrators and read-only viewers. Customer identities live under their organization.'
          : 'Everyone with an identity on the platform.'}
      />

      <Card className="overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              {['User', 'Platform role', 'Status', 'Last login', 'Created', ''].map((h, i) => (
                <TableHead key={h || i}>{h}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && <TableEmpty colSpan={6}><LoadingState /></TableEmpty>}
            {!loading && error && <TableEmpty colSpan={6}><ErrorState message={error} onRetry={reload} /></TableEmpty>}
            {!loading && !error && visiblePeople.length === 0 && (
              <TableEmpty colSpan={6}>
                <EmptyState icon={UsersIcon} title="No users yet" description="Identities appear here as people are invited and claim their accounts." />
              </TableEmpty>
            )}
            {visiblePeople.map((p) => {
              const isSelf = p.id === user?.id
              const canActOn = isAdmin && !isSelf
              return (
                <TableRow key={p.id} className="hover:bg-transparent">
                  <TableCell>
                    <div className="font-medium text-foreground">{p.email}</div>
                    {p.display_name && <div className="text-xs text-muted-foreground">{p.display_name}</div>}
                  </TableCell>
                  <TableCell><Badge tone={roleTone(p.platform_role)}>{roleLabel(p.platform_role)}</Badge></TableCell>
                  <TableCell><Badge tone={statusTone(p.status)}>{p.status}</Badge></TableCell>
                  <TableCell className="text-muted-foreground text-xs">
                    {p.last_login_at ? new Date(p.last_login_at).toLocaleDateString() : 'Never'}
                  </TableCell>
                  <TableCell className="text-muted-foreground text-xs">{new Date(p.created_at).toLocaleDateString()}</TableCell>
                  <TableCell className="text-right whitespace-nowrap" {...stopRowActivate}>
                    {isSelf ? (
                      <span className="text-xs text-muted-foreground">You</span>
                    ) : canActOn ? (
                      <RowActions label={`Actions for ${p.email}`}>
                        <DropdownMenuContent>
                          {p.platform_role !== 'admin' && (
                            <DropdownMenuItem onSelect={() => setPending({ person: p, next: 'admin' })}>Make platform admin</DropdownMenuItem>
                          )}
                          {p.platform_role !== 'viewer' && (
                            <DropdownMenuItem onSelect={() => setPending({ person: p, next: 'viewer' })}>Make viewer (read-only)</DropdownMenuItem>
                          )}
                          {p.platform_role !== null && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem destructive onSelect={() => setPending({ person: p, next: null })}>Remove platform access</DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </RowActions>
                    ) : (
                      <ChevronRight className="size-4 opacity-0" aria-hidden />
                    )}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </Card>

      {/* Promote / step-down — guarded by typing the person's email */}
      <ConfirmDestructive
        open={pending !== null}
        onOpenChange={(o) => { if (!o) setPending(null) }}
        title={copy?.title ?? ''}
        description={copy?.body ?? ''}
        confirmPhrase={pending?.person.email ?? ''}
        confirmLabel={copy?.label ?? 'Confirm'}
        variant={copy?.variant ?? 'default'}
        onConfirm={applyRole}
      />
    </div>
  )
}
